import { useMemo, useState } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import {
  FilterField,
  DateFilterValue,
  FilterValue,
} from '@/components/school-management/BimloaFilter/BimloaFilter';
import {
  StudentMetricsDashboardFilters,
  StudentMetricsDashboardTimeRange,
} from '@/school-management/types/generate-report';
import {
  TeacherAutocompleteItem,
  GroupAutocompleteItem,
  StudentAutocompleteItem,
} from '@/school-management/types/response/autocomplete';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import UserUtils from '@/utils/user';
import { skillsForReport, getSkillName } from './normalizeData';

const useStudentMetricsDashboard = () => {
  const { t } = useAdminTranslation();
  const { user } = useAppSelector(authSelector);
  const isTeacher = UserUtils.isTeacher(user);
  const [drillDownType, setDrillDownType] = useState<
    | ''
    | 'AvgStudentGrowth'
    | 'AvgStudentProgressAtCurrentLevel'
    | 'StudentLevelProgression'
    | 'AvgScores'
    | 'StudentsAcrossSkills'
  >('');

  const [filters, setFilters] = useState<Record<string, FilterValue>>({
    timeRange: { mode: 'none' },
    level: [],
    group: null,
    grade: [],
    teacher: null,
    ...skillsForReport.reduce((acc, sId) => {
      acc[getSkillName(sId)] = 'All';
      return acc;
    }, {} as any),
  });

  const grades: {
    [gradeName: string]: number[];
  } = useMemo(() => {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].reduce(
      (acc, g) => {
        acc[t(`grade_${g}`)] = [g];

        return acc;
      },
      {} as {
        [gradeName: string]: number[];
      }
    );
  }, [t]);

  const filterFields: FilterField[] = useMemo(() => {
    const gradeNames = Object.keys(grades || {});

    let fields: FilterField[] = [
      // {
      //   key: 'timeRange',
      //   label: t('studentMetricsDashboard.filters.timeRange'),
      //   options: ['last30', 'thisYear'],
      //   dateMode: true,
      // },
      {
        key: 'teacher',
        label: t('studentMetricsDashboard.filters.teacher') || 'Teacher Name',
        options: [],
        multi: true,
        autocompleteType: 'teachers',
        searchPlaceholder:
          t('studentMetricsDashboard.filters.teacherSearchPlaceholder') || 'Search teacher',
      },
      {
        key: 'group',
        label: t('studentMetricsDashboard.filters.group') || 'Class Name',
        options: [],
        multi: true,
        autocompleteType: 'groups',
        searchPlaceholder:
          t('studentMetricsDashboard.filters.groupSearchPlaceholder') || 'Search class',
      },
      {
        key: 'level',
        label: t('studentMetricsDashboard.filters.currentLevel'),
        options: ['All', '1', '2', '3', '4', '5'],
        multi: true,
        searchable: true,
        searchPlaceholder: t('studentMetricsDashboard.filters.currentLevelSearchPlaceholder') || '',
      },
      {
        key: 'grade',
        label: t('studentMetricsDashboard.filters.grade'),
        options: gradeNames,
        multi: true,
        searchable: true,
        searchPlaceholder: t('studentMetricsDashboard.filters.gradeSearchPlaceholder') || '',
      },
    ].filter(c => !isTeacher || c.key !== 'teacher') as FilterField[];

    if (drillDownType === 'AvgScores' || drillDownType === 'StudentsAcrossSkills') {
      fields = [
        {
          key: 'student',
          label: t('studentMetricsDashboard.filters.studentName') || 'Student Name',
          options: [],
          multi: true,
          autocompleteType: 'students',
          searchPlaceholder:
            t('studentMetricsDashboard.filters.studentNameSearchPlaceholder') || 'Search student',
        },
        ...fields,
        ...skillsForReport.reduce((acc, sId) => {
          const skillName = getSkillName(sId).toString();
          const skillKey =
            'skill' + skillName.charAt(0).toUpperCase() + skillName.slice(1).toLowerCase();
          acc.push({
            key: skillName,
            label: t(`studentMetricsDashboard.drillDown.skills.columns.${skillKey}`) || skillName,
            options: ['Pass', 'Fail'],
            optionLabels: {
              Pass: t('studentMetricsDashboard.filters.pass') || 'Pass',
              Fail: t('studentMetricsDashboard.filters.fail') || 'Fail',
            },
          });
          return acc;
        }, [] as FilterField[]),
      ];
    }

    return fields;
  }, [t, grades, isTeacher, drillDownType]);

  const dashboardFilter: StudentMetricsDashboardFilters = useMemo(() => {
    const dashboardFilter: StudentMetricsDashboardFilters = {
      // timeRange: StudentMetricsDashboardTimeRange.all_time,
    };

    // if (filters.timeRange) {
    //   switch ((filters.timeRange as DateFilterValue).mode) {
    //     case 'last7':
    //       dashboardFilter.timeRange = StudentMetricsDashboardTimeRange.last_7_days;
    //       break;
    //     case 'last30':
    //       dashboardFilter.timeRange = StudentMetricsDashboardTimeRange.last_30_days;
    //       break;
    //     case 'thisYear':
    //       dashboardFilter.timeRange = StudentMetricsDashboardTimeRange.this_year;
    //       break;
    //     default:
    //       dashboardFilter.timeRange = StudentMetricsDashboardTimeRange.all_time;
    //   }
    // }

    if (
      filters.level &&
      typeof filters.level !== 'string' &&
      (filters.level as string[]).length > 0
    ) {
      dashboardFilter.levelIds = (filters.level as string[]).map(l => Number(l));
    }

    if (filters.group) {
      const groupIds = (filters.group as GroupAutocompleteItem[]).map(g => g.groupId);

      if (groupIds.length > 0) {
        dashboardFilter.groupIds = groupIds;
      }
    }

    if (
      grades &&
      filters.grade &&
      typeof filters.grade !== 'string' &&
      (filters.grade as string[]).length > 0
    ) {
      const gradeIds = (filters.grade as string[]).reduce((acc, gradeName) => {
        const gradeIds = grades[gradeName] || [];

        return [...acc, ...gradeIds];
      }, [] as number[]);

      if (gradeIds.length > 0) {
        dashboardFilter.gradeIds = gradeIds;
      }
    }

    if (filters.teacher) {
      const teacherIds = (filters.teacher as TeacherAutocompleteItem[]).map(t => t.id);

      if (teacherIds.length > 0) {
        dashboardFilter.teacherIds = teacherIds;
      }
    }

    if (drillDownType === 'AvgScores' || drillDownType === 'StudentsAcrossSkills') {
      if (filters.student) {
        const studentIds = (filters.student as StudentAutocompleteItem[]).map(s => s.id);

        if (studentIds.length > 0) {
          dashboardFilter.studentIds = studentIds;
        }
      }

      const skillFilters = skillsForReport.reduce(
        (acc, sId) => {
          const skillName = getSkillName(sId).toString();

          if (
            filters[skillName] &&
            (filters[skillName] === 'Pass' || filters[skillName] === 'Fail')
          ) {
            acc.push({
              skillId: sId,
              performanceStatus: filters[skillName] as 'Pass' | 'Fail',
            });
          }

          return acc;
        },
        [] as {
          skillId: number;
          performanceStatus: 'Pass' | 'Fail';
        }[]
      );

      if (skillFilters.length > 0) {
        dashboardFilter.skillFilters = skillFilters;
      }
    }

    return dashboardFilter;
  }, [filters, grades, drillDownType]);

  return {
    drillDownType,
    setDrillDownType,
    filters,
    setFilters,
    filterFields,
    dashboardFilter,
  };
};

export default useStudentMetricsDashboard;
